<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { Button, Indicator, Tooltip } from "flowbite-svelte";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import {
    faTimes,
    faGripLinesVertical,
    faRotateRight,
    faMinimize,
    faArrowsLeftRight,
    faMaximize,
  } from "@fortawesome/free-solid-svg-icons";
  import {
    getDrawer,
    drawerStore,
    setDrawerWidth,
    setActiveDrawer,
    getDrawerWidth,
    type Drawer,
    closeDrawer as closeDrawerStore,
  } from "../../services/stores/side-drawer-store.js";
  import { selectedObjects } from "../../services/stores/selected-objects-store.js";

  $: isOpen = $drawerStore.isOpen;
  $: activeDrawer = $drawerStore.activeDrawer;
  $: drawerWidth = activeDrawer ? getDrawerWidth(activeDrawer) : 400;
  $: drawers = Object.values($drawerStore.drawers) as Drawer[];
  $: if (activeDrawer !== activeDrawerKey) {
    activeDrawerKey = activeDrawer;
    activeDrawerInstance = null;
  }

  let activeDrawerInstance: any;
  let activeDrawerKey: string | null = null;

  const dispatch = createEventDispatcher();

  function toggleDrawer(drawerKey: string) {
    drawerStore.update((store) => ({
      ...store,
      isOpen: !(store.isOpen && store.activeDrawer === drawerKey),
      activeDrawer: drawerKey,
    }));
    dispatch("drawerToggle", {
      isOpen: $drawerStore.isOpen,
      activeDrawer: drawerKey,
    });
  }

  function closeDrawer() {
    closeDrawerStore();
    dispatch("drawerToggle", { isOpen: false, activeDrawer: "" });
  }

  function refreshDrawer() {
    if (
      activeDrawerInstance &&
      typeof activeDrawerInstance.refresh === "function"
    ) {
      activeDrawerInstance.refresh();
    }
  }

  let resizing = false;
  let startX: number;
  let startWidth: number;

  function handleResize(event: MouseEvent) {
    if (!resizing) return;

    const dx = startX - event.clientX;
    const newWidth = Math.max(400, Math.min(1400, startWidth + dx));
    drawerWidth = newWidth;
    if (activeDrawer) {
      setDrawerWidth(activeDrawer, drawerWidth);
      dispatch("resize", { width: drawerWidth });
    }
  }

  function setDrawerSize(size: "min" | "mid" | "max") {
    const minWidth = 550;
    const maxWidth = Math.min(1400, window.innerWidth * 0.7); // 70% of window width or 1400px
    const midWidth = minWidth + (maxWidth - minWidth) / 2;

    const newWidth =
      size === "min" ? minWidth : size === "max" ? maxWidth : midWidth;

    drawerWidth = newWidth;
    if (activeDrawer) {
      setDrawerWidth(activeDrawer, newWidth);
      dispatch("resize", { width: newWidth });
    }
  }
</script>

<div class="drawer-system" class:open={isOpen}>
  <div class="drawer-buttons">
    {#each drawers as drawer}
      {#if drawer.isVisible && drawer.icon}
        <Button
          id={drawer.key}
          class="toolbar-button relative"
          on:click={() => toggleDrawer(drawer.key)}
        >
          <FontAwesomeIcon icon={drawer.icon} />
          {#if drawer.key === "selected-documents" && $selectedObjects.documents.length > 0}
            <Indicator placement="top-right" class="drawer-indicator">{Math.min($selectedObjects.documents.length, 999)}</Indicator>
          {/if}
        </Button>
        <!-- {#if drawer.key === "selected-documents"}
          <Tooltip
            class="tooltip-popover"
            triggeredBy="#{drawer.key}"
            placement="left">{drawer.description}</Tooltip
          >
        {/if} -->
      {/if}
    {/each}
  </div>
  {#if isOpen && activeDrawer}
    <div class="drawer-content-wrapper" style="min-width: {drawerWidth}px">
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <!-- <div class="resize-handle" on:mousedown={startResize}>
        <FontAwesomeIcon icon={faGripLinesVertical} class="grip-icon" />
      </div> -->
      <div class="drawer-content">
        <div class="drawer-header">
          <div class="drawer-title-container">
            <h2>{getDrawer(activeDrawer)?.title ?? ""}</h2>

            {#if activeDrawer !== "selected-documents"}
              <!-- {#if getDrawer(activeDrawer)?.supportsRefresh}
                <Button class="drawer-header-button" on:click={refreshDrawer}>
                  <FontAwesomeIcon icon={faRotateRight} />
                </Button>
              {/if} -->
              <Button
                class="drawer-header-button"
                on:click={() => setDrawerSize("max")}
              >
                <FontAwesomeIcon icon={faMaximize} />
              </Button>
              <Button
                class="drawer-header-button"
                on:click={() => setDrawerSize("mid")}
              >
                <FontAwesomeIcon icon={faArrowsLeftRight} />
              </Button>
              <Button
                class="drawer-header-button"
                on:click={() => setDrawerSize("min")}
              >
                <FontAwesomeIcon icon={faMinimize} />
              </Button>
            {/if}
          </div>
          <Button class="drawer-header-button" on:click={closeDrawer}>
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
        {#key activeDrawer}
          <svelte:component
            this={getDrawer(activeDrawer)?.component}
            {...getDrawer(activeDrawer)?.props}
            bind:this={activeDrawerInstance}
          />
        {/key}
      </div>
    </div>
  {/if}
</div>
